// ============================================================================
// Client Service Status Configuration
// ============================================================================
// Single source of truth for the user-selectable client_services statuses and
// how each maps onto the DB columns (service_status / is_enabled / opted_in).
// Consumed by the client services grid, the service edit modal, and the
// Edit Services modal.

/** Status items for status dropdowns (opt-in services only) */
export const STATUS_ITEMS = [
    { id: 'active', label: 'Active', color: 'var(--color-success)' },
    { id: 'on-request', label: 'On-Request', color: 'var(--color-warning)' },
    { id: 'paused', label: 'Paused', color: 'var(--color-warning)' },
    { id: 'inactive', label: 'Inactive', color: 'var(--color-text-muted)' }
];

/**
 * Full display labels for EVERY client_services.service_status value, including
 * legacy aliases (available -> 'Inactive'). Single source - import this instead
 * of re-typing the map (it was duplicated byte-for-byte in two files). The
 * editable STATUS_ITEMS above is the user-selectable SUBSET of these.
 */
export const SERVICE_STATUS_LABELS = {
    active: 'Active',
    'ad-hoc': 'Ad-Hoc',
    'on-request': 'On-Request',
    paused: 'Paused',
    inactive: 'Inactive',
    pending: 'Pending Confirmation',
    not_needed: 'Not Needed',
    available: 'Inactive'
};

/**
 * Human label for a client service status. Single source.
 * @param {string} status
 * @returns {string}
 */
export function formatServiceStatus(status) {
    return SERVICE_STATUS_LABELS[status] || status || '';
}

/**
 * Client-service billing methods (billed_via_type). Single source for the
 * cost-allocation picker and the display resolvers. NOTE: 'linked_service' has a
 * DYNAMIC label ('Billed With: <service>') that callers with the target service
 * name render themselves; formatBilledViaType returns the generic fallback.
 */
export const BILLING_METHODS = [
    { value: 'standard',       label: 'Standard' },
    { value: 'retainer',       label: 'Retainer' },
    { value: 'linked_service', label: 'Linked Service' },
    { value: 'pro_bono',       label: 'Pro-Bono' }
];

const BILLING_METHOD_LABELS = Object.fromEntries(BILLING_METHODS.map(m => [m.value, m.label]));

/**
 * Static label for a billed_via_type (unknown -> 'Standard'). For
 * 'linked_service' this is the generic 'Linked Service'; callers that have the
 * linked service name render the dynamic 'Billed With: <name>' themselves.
 * @param {string} type
 * @returns {string}
 */
export function formatBilledViaType(type) {
    return BILLING_METHOD_LABELS[type] || 'Standard';
}

/**
 * Link modes for 'linked_service' billing (client_services.billing_link_mode) -
 * the period gate for the carried-time sweep. Single source for the value
 * union, display labels, and the default: same_period sweeps carried time onto
 * the matching-year bill; next_unbilled onto the next open bill of any year.
 */
export const BILLING_LINK_MODES = [
    { value: 'same_period',   label: 'Same period' },
    { value: 'next_unbilled', label: 'Next unbilled' }
];

export const DEFAULT_BILLING_LINK_MODE = 'same_period';

const BILLING_LINK_MODE_LABELS = Object.fromEntries(BILLING_LINK_MODES.map(m => [m.value, m.label]));

/**
 * Human label for a billing_link_mode (null/unknown -> the default's label).
 * @param {string|null} mode
 * @returns {string}
 */
export function formatBillingLinkMode(mode) {
    return BILLING_LINK_MODE_LABELS[mode] || BILLING_LINK_MODE_LABELS[DEFAULT_BILLING_LINK_MODE];
}

/** Maps dropdown status id to DB field values */
export const STATUS_DB_MAP = {
    'active': { service_status: 'active', is_enabled: true, opted_in: true },
    'on-request': { service_status: 'on-request', is_enabled: true, opted_in: false },
    'paused': { service_status: 'paused', is_enabled: true, opted_in: true },
    'inactive': { service_status: 'inactive', is_enabled: false, opted_in: false }
};

/**
 * The status a client_services row is IN, read from its flags - the inverse of
 * STATUS_DB_MAP and the client mirror of `v_client_services.is_currently_active`
 * / `service_status_computed`. The flags are what every gate in the app acts on;
 * the `service_status` column has drifted on real rows (an 'active' label over
 * opted_in=false / is_enabled=false, and the reverse), so a picker that labelled
 * from the column would print "Active" inside its inactive group.
 * @param {{ opted_in: boolean, is_enabled: boolean }} row
 * @returns {{ id: 'active'|'on-request'|'inactive', isCurrentlyActive: boolean }}
 */
export function computedServiceStatus({ opted_in, is_enabled }) {
    const isCurrentlyActive = Boolean(opted_in && is_enabled);
    if (isCurrentlyActive) return { id: 'active', isCurrentlyActive };
    return { id: is_enabled ? 'on-request' : 'inactive', isCurrentlyActive };
}

/** Maps display status to dropdown id for current-value matching.
 *  Statuses absent from STATUS_ITEMS keep their own id so any pick is a
 *  genuine change rather than a silent rewrite to another status. */
export const STATUS_DISPLAY_TO_ID = {
    'Active': 'active',
    'On-Request': 'on-request',
    'Paused': 'paused',
    'Inactive': 'inactive',
    'Not Needed': 'not-needed',
    'Pending Confirmation': 'pending',
    'Ad-Hoc': 'ad-hoc'
};
